import { useMemo, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { Video } from 'lucide-react';
import { type VideoMetadata, formatVideoDuration } from '@/lib/video-metadata';
import { type UploadPreviewCandidate, uploadPreviewCandidates } from '@/lib/upload/uploadPreviewCandidates';

interface UploadVideoPreviewProps {
  videoMetadata: VideoMetadata | null;
  // Read by the modal's "use current position" time-range buttons.
  videoRef: RefObject<HTMLVideoElement>;
}

const candidateKey = (candidate: UploadPreviewCandidate) => `${candidate.kind}:${candidate.src}`;

// Preview of the video being uploaded or imported. Tries each candidate in
// order and drops one that fails to load (or whose picture the browser cannot
// decode); with nothing left it shows a compact info row instead of an empty
// or broken player. Media loads without crossOrigin: nothing reads its pixels,
// and the attribute makes hosts without CORS headers fail.
export function UploadVideoPreview({ videoMetadata, videoRef }: UploadVideoPreviewProps) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());

  const candidates = useMemo(() => uploadPreviewCandidates(videoMetadata), [videoMetadata]);
  const current = candidates.find((candidate) => !failed.has(candidateKey(candidate)));
  const markFailed = (candidate: UploadPreviewCandidate) => {
    setFailed((previous) => new Set(previous).add(candidateKey(candidate)));
  };

  const isDropbox = videoMetadata?.platform === 'dropbox';
  const duration = videoMetadata ? formatVideoDuration(videoMetadata.duration) : '';

  if (!current) {
    const details = videoMetadata
      ? [
        isDropbox ? 'Dropbox' : null,
        duration,
        videoMetadata.width && videoMetadata.height ? `${videoMetadata.width} × ${videoMetadata.height}` : null,
      ].filter(Boolean).join(' · ')
      : t('uploadModal.yourVideoWillAppearHere');

    return (
      <div className="flex flex-col gap-4 items-center justify-center">
        <div className="w-full max-w-[520px] flex items-center gap-3 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5 p-3 sm:p-4">
          <Video className="w-8 h-8 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="break-all text-sm sm:text-base font-medium text-primary">
              {videoMetadata?.title || (videoMetadata ? t('uploadModal.video') : t('uploadModal.videoPreview'))}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground">{details}</p>
          </div>
        </div>
        {isDropbox && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <p className="text-xs text-primary text-center">{t('dropbox.previewAfterImport')}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 items-center justify-center">
      <div className="w-full max-w-[520px] aspect-video bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl overflow-hidden border border-primary/20 shadow-glow">
        {current.kind === 'video' ? (
          <div className="w-full h-full relative">
            <video
              key={current.src}
              ref={videoRef}
              src={current.src}
              poster={current.poster}
              controls
              controlsList="nodownload"
              className="w-full h-full object-cover"
              preload="metadata"
              onError={() => markFailed(current)}
              onLoadedMetadata={(event) => {
                // Metadata loaded but no picture: the browser cannot decode
                // this video track (e.g. HEVC without a decoder).
                if (event.currentTarget.videoWidth === 0) markFailed(current);
              }}
            >
              {t('uploadModal.browserNotSupportVideo')}
            </video>
            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs font-medium">
              {duration}
            </div>
          </div>
        ) : (
          <div className="w-full h-full relative">
            <img
              key={current.src}
              src={current.src}
              alt="Video thumbnail"
              className="w-full h-full object-cover"
              onError={() => markFailed(current)}
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="bg-black/50 text-white px-3 py-1 rounded-lg text-sm font-medium">
                {duration}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
        <p className="text-xs text-primary text-center">{t('uploadModal.previewLowRes')}</p>
      </div>
    </div>
  );
}
